// html2canvas-pro, not html2canvas: the original throws on Tailwind v4's oklch() /
// color-mix() colours ("Attempting to parse an unsupported color function").
import html2canvas from "html2canvas-pro";
import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { Pencil, Square, ArrowUpRight, Trash2, Camera, Sparkles, ChevronDown, ChevronRight, Info } from "lucide-react";
import { api } from "../lib/api";
import { logError } from "../lib/logger.js";
import { MODULE_LABELS } from "../lib/moduleRoutes.js";
import { collectIssueContext, type IssueContext } from "../lib/issueContext.js";
import { AsyncButton } from "./ui/async-button.js";
import { SelectField } from "./ui/select-field.js";
import { FileInput } from "./ui/file-input.js";
import { Input } from "./ui/input.js";
import { Textarea } from "./ui/textarea.js";
import { Label } from "./ui/label.js";
import { Badge } from "./ui/badge.js";
import { VoiceInputButton } from "./ui/voice-input-button.js";

interface DraftReview {
  suggested_title: string;
  rewritten_description: string;
  clarifying_questions: string[];
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  prefill: { module?: string };
}

type Tool = "pen" | "rect" | "arrow";

const COLORS = ["var(--color-error-accent)", "var(--color-warning-accent)", "var(--color-info-accent)", "var(--color-text)"];
const HRMS_MODULES = Object.entries(MODULE_LABELS);

/** Appends a dictated phrase to a field's text, on a new sentence. */
function appendSpoken(current: string, phrase: string): string {
  const spoken = phrase.charAt(0).toUpperCase() + phrase.slice(1);
  if (!current.trim()) return spoken;
  return /[.!?\n]\s*$/.test(current) ? `${current.trimEnd()} ${spoken}` : `${current.trimEnd()}. ${spoken}`;
}

function drawArrow(
  ctx: CanvasRenderingContext2D,
  x1: number, y1: number,
  x2: number, y2: number,
) {
  const headLen = 14;
  const angle = Math.atan2(y2 - y1, x2 - x1);
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(x2, y2);
  ctx.lineTo(x2 - headLen * Math.cos(angle - Math.PI / 6), y2 - headLen * Math.sin(angle - Math.PI / 6));
  ctx.lineTo(x2 - headLen * Math.cos(angle + Math.PI / 6), y2 - headLen * Math.sin(angle + Math.PI / 6));
  ctx.closePath();
  ctx.fill();
}

export function IssueReportPanel({ isOpen, onClose, prefill }: Props) {
  const [form, setForm] = useState({
    title: "",
    type: "bug",
    module: "",
    priority: "medium",
    description: "",
    stepsToReproduce: "",
  });
  const [snapshot, setSnapshot] = useState<HTMLCanvasElement | null>(null);
  const [captureLoading, setCaptureLoading] = useState(false);
  const [annotationMode, setAnnotationMode] = useState(false);
  const [tool, setTool] = useState<Tool>("pen");
  const [color, setColor] = useState(COLORS[0]!);
  const [undoStack, setUndoStack] = useState<ImageData[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [showSteps, setShowSteps] = useState(false);
  const [review, setReview] = useState<DraftReview | null>(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewAccepted, setReviewAccepted] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [context, setContext] = useState<IssueContext | null>(null);
  const [showContext, setShowContext] = useState(false);
  const [interim, setInterim] = useState<{ field: "description" | "stepsToReproduce"; text: string } | null>(null);
  const [voiceError, setVoiceError] = useState<string | null>(null);

  const annotationCanvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawing = useRef(false);
  const startPos = useRef({ x: 0, y: 0 });
  const lastUndoData = useRef<ImageData | null>(null);

  useEffect(() => {
    if (isOpen) {
      const pageTitle = document.title.replace(/ \| HRMS$/, "");
      setForm({
        title: pageTitle === "HRMS" ? "" : pageTitle,
        type: "bug",
        module: prefill.module ?? "",
        priority: "medium",
        description: "",
        stepsToReproduce: "",
      });
      setSnapshot(null);
      setAnnotationMode(false);
      setUndoStack([]);
      setError(null);
      setIsDirty(false);
      setShowSteps(false);
      setReview(null);
      setReviewAccepted(false);
      setFiles([]);
      // Captured when the panel opens, i.e. on the page the reporter is looking at.
      setContext(collectIssueContext());
      setShowContext(false);
      setInterim(null);
      setVoiceError(null);
    }
  }, [isOpen, prefill.module]);

  function dictate(field: "description" | "stepsToReproduce") {
    return {
      onTranscript: (text: string) => {
        setForm(f => ({ ...f, [field]: appendSpoken(f[field], text) }));
        setIsDirty(true);
        setVoiceError(null);
      },
      onInterim: (text: string) => setInterim(text ? { field, text } : null),
      onError: (message: string) => setVoiceError(message),
    };
  }

  function setField(field: string, value: string) {
    setForm(f => ({ ...f, [field]: value }));
    setIsDirty(true);
  }

  function handleClose() {
    if (annotationMode) return;
    if (isDirty && !window.confirm("Discard this report?")) return;
    onClose();
  }

  async function captureSnapshot() {
    setCaptureLoading(true);
    try {
      const canvas = await html2canvas(document.body, {
        useCORS: true,
        scale: window.devicePixelRatio * 0.5,
        ignoreElements: (el: Element) =>
          el.id === "hrms-issue-panel" ||
          el.id === "hrms-report-btn" ||
          el.id === "hrms-panel-backdrop",
      });
      setSnapshot(canvas);
    } catch (err) {
      logError("IssueReportPanel.captureSnapshot", err);
      setError("Snapshot failed — you can paste or upload a screenshot manually.");
    } finally {
      setCaptureLoading(false);
    }
  }

  function enterAnnotation() {
    setAnnotationMode(true);
    setUndoStack([]);
    setTimeout(() => {
      const canvas = annotationCanvasRef.current;
      if (!canvas || !snapshot) return;
      canvas.width = snapshot.width;
      canvas.height = snapshot.height;
      const ctx = canvas.getContext("2d");
      if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    }, 0);
  }

  function exitAnnotation() {
    setAnnotationMode(false);
  }

  function getScaledPos(e: React.MouseEvent<HTMLCanvasElement>) {
    const canvas = annotationCanvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) * (canvas.width / rect.width),
      y: (e.clientY - rect.top) * (canvas.height / rect.height),
    };
  }

  function onMouseDown(e: React.MouseEvent<HTMLCanvasElement>) {
    isDrawing.current = true;
    const pos = getScaledPos(e);
    startPos.current = pos;
    const canvas = annotationCanvasRef.current!;
    const ctx = canvas.getContext("2d")!;
    lastUndoData.current = ctx.getImageData(0, 0, canvas.width, canvas.height);
    if (tool === "pen") {
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
    }
  }

  function onMouseMove(e: React.MouseEvent<HTMLCanvasElement>) {
    if (!isDrawing.current) return;
    const canvas = annotationCanvasRef.current!;
    const ctx = canvas.getContext("2d")!;
    const pos = getScaledPos(e);
    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    ctx.lineWidth = 3;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    if (tool === "pen") {
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
    } else if (tool === "rect" || tool === "arrow") {
      if (lastUndoData.current) ctx.putImageData(lastUndoData.current, 0, 0);
      if (tool === "rect") {
        ctx.strokeRect(
          startPos.current.x, startPos.current.y,
          pos.x - startPos.current.x, pos.y - startPos.current.y,
        );
      } else {
        drawArrow(ctx, startPos.current.x, startPos.current.y, pos.x, pos.y);
      }
    }
  }

  function onMouseUp() {
    if (!isDrawing.current) return;
    isDrawing.current = false;
    const canvas = annotationCanvasRef.current!;
    const ctx = canvas.getContext("2d")!;
    const data = ctx.getImageData(0, 0, canvas.width, canvas.height);
    setUndoStack(s => [...s, data]);
    lastUndoData.current = null;
  }

  function undo() {
    const canvas = annotationCanvasRef.current!;
    const ctx = canvas.getContext("2d")!;
    if (undoStack.length === 0) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      return;
    }
    const prev = undoStack[undoStack.length - 2];
    if (prev) {
      ctx.putImageData(prev, 0, 0);
    } else {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    setUndoStack(s => s.slice(0, -1));
  }

  async function handleImprove() {
    setReviewLoading(true);
    setReview(null);
    try {
      const { data } = await api.post<DraftReview>("/issues/draft-review", form);
      setReview(data);
    } catch (err) {
      logError("IssueReportPanel.handleImprove", err);
      setReview({ suggested_title: "", rewritten_description: "", clarifying_questions: ["AI improve is temporarily unavailable — please try again later."] });
    }
    setReviewLoading(false);
  }

  function acceptReview() {
    if (!review) return;
    setForm(f => ({ ...f, title: review.suggested_title || f.title, description: review.rewritten_description || f.description }));
    setReviewAccepted(true);
    setReview(null);
    setIsDirty(true);
  }

  const uploadFile = useCallback(async (issueId: string, file: File) => {
    const { data: presign } = await api.post<{ uploadUrl: string; s3Key: string }>(
      `/issues/${issueId}/attachments/presign`,
      { filename: file.name, mimeType: file.type, sizeBytes: file.size },
    );
    await axios.put(presign.uploadUrl, file, { headers: { "Content-Type": file.type } });
    await api.post(`/issues/${issueId}/attachments`, {
      filename: file.name, s3Key: presign.s3Key, mimeType: file.type, sizeBytes: file.size,
    });
  }, []);

  function clearAnnotation() {
    const canvas = annotationCanvasRef.current!;
    const ctx = canvas.getContext("2d")!;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setUndoStack([]);
  }

  function getAnnotatedBlob(): Promise<Blob> {
    const composite = document.createElement("canvas");
    composite.width = snapshot!.width;
    composite.height = snapshot!.height;
    const ctx = composite.getContext("2d")!;
    ctx.drawImage(snapshot!, 0, 0);
    if (annotationCanvasRef.current) ctx.drawImage(annotationCanvasRef.current, 0, 0);
    return new Promise(resolve => composite.toBlob(resolve as BlobCallback, "image/png"));
  }

  async function handleSubmit() {
    if (!form.title.trim() || !form.description.trim()) {
      setError("Title and description are required.");
      throw new Error("validation");
    }
    setSubmitting(true);
    setError(null);
    try {
      const issueBody: Record<string, unknown> = {
        ...(context ?? collectIssueContext()),
        title: form.title.trim(),
        type: form.type,
        description: form.description.trim(),
        priority: form.priority,
      };
      if (form.module) issueBody["module"] = form.module;
      if (form.stepsToReproduce.trim()) issueBody["stepsToReproduce"] = form.stepsToReproduce.trim();

      const { data: issue } = await api.post<{ id: string }>("/issues", issueBody);

      if (snapshot) {
        const blob = await getAnnotatedBlob();
        const { data: presign } = await api.post<{ uploadUrl: string; s3Key: string }>(
          `/issues/${issue.id}/attachments/presign`,
          { filename: "screenshot.png", mimeType: "image/png", sizeBytes: blob.size },
        );
        await axios.put(presign.uploadUrl, blob, { headers: { "Content-Type": "image/png" } });
        await api.post(`/issues/${issue.id}/attachments`, {
          filename: "screenshot.png",
          s3Key: presign.s3Key,
          mimeType: "image/png",
          sizeBytes: blob.size,
        });
      }

      for (const f of files) {
        try { await uploadFile(issue.id, f); } catch (err) { logError("IssueReportPanel.uploadFile", err); }
      }

      window.dispatchEvent(new CustomEvent("hrms:issue:submitted", { detail: issue }));
      onClose();
    } catch (err: unknown) {
      logError("IssueReportPanel.handleSubmit", err);
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes("401")) {
        setError("Session expired — please refresh and sign in again.");
      } else {
        setError(`Submit failed: ${msg}`);
      }
      throw err;
    } finally {
      setSubmitting(false);
    }
  }

  const panelStyle: React.CSSProperties = {
    position: "fixed",
    top: 0,
    right: 0,
    bottom: 0,
    width: "min(480px, 100vw)",
    zIndex: 950,
    background: "var(--color-surface-0, #fff)",
    boxShadow: "-4px 0 24px rgba(0,0,0,0.18)",
    display: "flex",
    flexDirection: "column",
    transform: isOpen ? "translateX(0)" : "translateX(100%)",
    transition: "transform 0.25s cubic-bezier(0.4,0,0.2,1)",
  };

  return (
    <>
      {isOpen && (
        <div
          id="hrms-panel-backdrop"
          onClick={handleClose}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 899,
            background: "rgba(0,0,0,0.35)",
          }}
        />
      )}
      <div id="hrms-issue-panel" style={panelStyle}>
        {/* Header */}
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 20px",
          height: 56,
          borderBottom: "1px solid var(--color-border, #e5e7eb)",
          flexShrink: 0,
        }}>
          <span style={{ fontWeight: 600, fontSize: 15 }}>Report Issue</span>
          <button
            onClick={handleClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: 20,
              color: "var(--color-text-secondary, #6b7280)",
              lineHeight: 1,
              padding: "4px 6px",
            }}
          >
            ×
          </button>
        </div>

        {/* Scrollable body */}
        <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column" }}>
          {annotationMode ? (
            /* ── Annotation Mode ── */
            <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
              <div style={{
                display: "flex",
                gap: 8,
                padding: "10px 14px",
                borderBottom: "1px solid var(--color-border, #e5e7eb)",
                flexWrap: "wrap",
                alignItems: "center",
                flexShrink: 0,
              }}>
                {(["pen", "rect", "arrow"] as Tool[]).map(t => (
                  <button
                    key={t}
                    onClick={() => setTool(t)}
                    style={{
                      padding: "4px 10px",
                      borderRadius: 6,
                      border: "1px solid var(--color-border, #e5e7eb)",
                      background: tool === t ? "var(--color-brand-500, #6366f1)" : "transparent",
                      color: tool === t ? "#fff" : "inherit",
                      cursor: "pointer",
                      fontSize: 13,
                      fontWeight: 500,
                    }}
                  >
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                      {t === "pen" ? <><Pencil size={14} /> Pen</> : t === "rect" ? <><Square size={14} /> Rect</> : <><ArrowUpRight size={14} /> Arrow</>}
                    </span>
                  </button>
                ))}
                <div style={{ display: "flex", gap: 6, marginLeft: 4 }}>
                  {COLORS.map(c => (
                    <button
                      key={c}
                      onClick={() => setColor(c)}
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        background: c,
                        border: color === c ? "3px solid var(--color-brand-500,#6366f1)" : "2px solid transparent",
                        cursor: "pointer",
                        outline: color === c ? "2px solid #fff" : "none",
                        outlineOffset: -3,
                      }}
                    />
                  ))}
                </div>
                <button
                  onClick={undo}
                  style={{ padding: "4px 8px", fontSize: 13, cursor: "pointer", border: "1px solid var(--color-border,#e5e7eb)", borderRadius: 6, background: "transparent" }}
                >
                  ↩ Undo
                </button>
                <button
                  onClick={clearAnnotation}
                  style={{ padding: "4px 8px", fontSize: 13, cursor: "pointer", border: "1px solid var(--color-border,#e5e7eb)", borderRadius: 6, background: "transparent" }}
                >
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Trash2 size={14} /> Clear</span>
                </button>
                <button
                  onClick={exitAnnotation}
                  style={{
                    padding: "4px 12px",
                    fontSize: 13,
                    cursor: "pointer",
                    border: "none",
                    borderRadius: 6,
                    background: "var(--color-brand-500,#6366f1)",
                    color: "#fff",
                    fontWeight: 600,
                    marginLeft: "auto",
                  }}
                >
                  Done
                </button>
              </div>
              <div style={{ position: "relative", flex: 1, overflow: "hidden", background: "#111" }}>
                {snapshot && (
                  <img
                    src={snapshot.toDataURL()}
                    alt="snapshot"
                    style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain" }}
                  />
                )}
                <canvas
                  ref={annotationCanvasRef}
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    cursor: "crosshair",
                  }}
                  onMouseDown={onMouseDown}
                  onMouseMove={onMouseMove}
                  onMouseUp={onMouseUp}
                  onMouseLeave={onMouseUp}
                />
              </div>
            </div>
          ) : (
            /* ── Normal form view ── */
            <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: 14 }}>
              {/* Snapshot section */}
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--color-text-secondary,#6b7280)", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  Screenshot
                </div>
                {snapshot ? (
                  <div>
                    <div style={{ position: "relative", borderRadius: 8, overflow: "hidden", border: "1px solid var(--color-border,#e5e7eb)", marginBottom: 8 }}>
                      <img
                        src={snapshot.toDataURL()}
                        alt="page snapshot"
                        style={{ width: "100%", display: "block", maxHeight: 180, objectFit: "cover", objectPosition: "top" }}
                      />
                      {annotationCanvasRef.current && undoStack.length > 0 && (
                        <div style={{
                          position: "absolute",
                          inset: 0,
                          backgroundImage: `url(${annotationCanvasRef.current.toDataURL()})`,
                          backgroundSize: "100% 100%",
                        }} />
                      )}
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={enterAnnotation} style={secondaryBtnStyle}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Pencil size={14} /> Annotate</span>
                      </button>
                      <button onClick={() => { setSnapshot(null); setUndoStack([]); }} style={secondaryBtnStyle}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Trash2 size={14} /> Remove</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={captureSnapshot}
                    disabled={captureLoading}
                    style={{
                      width: "100%",
                      padding: "10px 0",
                      borderRadius: 8,
                      border: "2px dashed var(--color-border,#e5e7eb)",
                      background: "transparent",
                      cursor: captureLoading ? "wait" : "pointer",
                      fontSize: 14,
                      color: "var(--color-text-secondary,#6b7280)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                    }}
                  >
                    {captureLoading ? "Capturing…" : <><Camera size={14} /> Capture current page</>}
                  </button>
                )}
              </div>

              {/* Title */}
              <div>
                <Label htmlFor="issue-title">Title *</Label>
                <Input
                  id="issue-title"
                  value={form.title}
                  onChange={e => setField("title", e.target.value)}
                  placeholder="Short summary of the issue"
                />
              </div>

              {/* Type + Priority row */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div>
                  <Label>Type</Label>
                  <SelectField value={form.type} onChange={e => setField("type", e.target.value)}>
                    <option value="bug">Bug</option>
                    <option value="issue">Issue</option>
                    <option value="feature_request">Feature Request</option>
                  </SelectField>
                </div>
                <div>
                  <Label>Priority</Label>
                  <SelectField value={form.priority} onChange={e => setField("priority", e.target.value)}>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </SelectField>
                </div>
              </div>

              {/* Module — prefilled from the page the panel was opened on */}
              <div>
                <Label>Module{prefill.module && form.module === prefill.module ? " (detected from this page)" : ""}</Label>
                <SelectField value={form.module} onChange={e => setField("module", e.target.value)}>
                  <option value="">— none —</option>
                  {HRMS_MODULES.map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </SelectField>
              </div>

              {/* Description */}
              <div>
                <div className="flex items-end justify-between gap-2">
                  <Label htmlFor="issue-description">Description *</Label>
                  <VoiceInputButton label="description" className="mb-1" {...dictate("description")} />
                </div>
                <Textarea
                  id="issue-description"
                  value={form.description}
                  onChange={e => setField("description", e.target.value)}
                  placeholder="What happened? What did you expect? Or press Speak and say it."
                  rows={5}
                  resize="vertical"
                />
                {interim?.field === "description" && (
                  <p className="mt-1 text-xs italic text-surface-500" aria-live="polite">{interim.text}…</p>
                )}
                {voiceError && <p className="mt-1 text-xs text-error">{voiceError}</p>}
              </div>

              {/* AI Improve */}
              {!reviewAccepted && (
                <div>
                  <button
                    type="button"
                    onClick={handleImprove}
                    disabled={reviewLoading || !form.description.trim()}
                    style={{
                      padding: "6px 12px", fontSize: 12, fontWeight: 600,
                      background: "var(--color-brand-50,#eef2ff)", color: "var(--color-brand-700,#4338ca)",
                      border: "1px solid var(--color-brand-200,#c7d2fe)", borderRadius: 6, cursor: "pointer",
                    }}
                  >
                    {reviewLoading ? "Improving…" : <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Sparkles size={14} /> Improve my report</span>}
                  </button>
                </div>
              )}
              {review && !reviewAccepted && (
                <div style={{ background: "var(--color-surface-50,#f9fafb)", border: "1px solid var(--color-border,#e5e7eb)", borderRadius: 8, padding: 12, fontSize: 13 }}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Suggested improvements</div>
                  {review.suggested_title && (
                    <div style={{ marginBottom: 6 }}>
                      <span style={{ color: "var(--color-text-secondary,#6b7280)" }}>Title: </span>
                      <span>{review.suggested_title}</span>
                    </div>
                  )}
                  {review.rewritten_description && (
                    <div style={{ marginBottom: 8, whiteSpace: "pre-wrap" }}>{review.rewritten_description}</div>
                  )}
                  {review.clarifying_questions.length > 0 && (
                    <ul style={{ margin: "0 0 8px", paddingLeft: 18 }}>
                      {review.clarifying_questions.map((q, i) => (
                        <li key={i} style={{ color: "var(--color-text-secondary,#6b7280)", marginBottom: 2, fontSize: 12 }}>{q}</li>
                      ))}
                    </ul>
                  )}
                  <div style={{ display: "flex", gap: 8 }}>
                    <button type="button" onClick={acceptReview} style={{ padding: "4px 10px", fontSize: 12, background: "var(--color-brand-500,#6366f1)", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}>
                      Accept
                    </button>
                    <button type="button" onClick={() => setReview(null)} style={{ padding: "4px 10px", fontSize: 12, background: "none", color: "var(--color-text-secondary,#6b7280)", border: "1px solid var(--color-border,#e5e7eb)", borderRadius: 6, cursor: "pointer" }}>
                      Ignore
                    </button>
                  </div>
                </div>
              )}

              {/* Steps to reproduce — collapsible */}
              <div>
                <button
                  type="button"
                  onClick={() => setShowSteps(s => !s)}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontSize: 13,
                    color: "var(--color-brand-500,#6366f1)",
                    padding: 0,
                    fontWeight: 500,
                  }}
                >
                  {showSteps ? "▼" : "▶"} Steps to reproduce (optional)
                </button>
                {showSteps && (
                  <div className="mt-2">
                    <div className="flex justify-end">
                      <VoiceInputButton label="steps" className="mb-1" {...dictate("stepsToReproduce")} />
                    </div>
                    <Textarea
                      value={form.stepsToReproduce}
                      onChange={e => setField("stepsToReproduce", e.target.value)}
                      placeholder={"1. Go to…\n2. Click…\n3. See error"}
                      rows={4}
                      resize="vertical"
                    />
                    {interim?.field === "stepsToReproduce" && (
                      <p className="mt-1 text-xs italic text-surface-500" aria-live="polite">{interim.text}…</p>
                    )}
                  </div>
                )}
              </div>

              {/* Auto-captured details — sent with the report, nothing to type */}
              {context && (
                <div className="rounded-lg border border-surface-200 bg-surface-50 p-3 text-xs">
                  <button
                    type="button"
                    onClick={() => setShowContext(s => !s)}
                    className="flex w-full items-center gap-1.5 text-left font-semibold text-surface-700"
                    aria-expanded={showContext}
                  >
                    {showContext ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    <Info size={14} />
                    Auto-captured details
                    <span className="ml-auto flex gap-1">
                      <Badge tone="outline">{context.browser}</Badge>
                      {context.consoleErrors.length > 0 && (
                        <Badge tone="error">{context.consoleErrors.length} console error{context.consoleErrors.length === 1 ? "" : "s"}</Badge>
                      )}
                    </span>
                  </button>
                  {showContext && (
                    <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-surface-600">
                      <dt className="font-medium">Page</dt><dd className="break-all">{context.pageUrl}</dd>
                      <dt className="font-medium">Module</dt><dd>{form.module ? (MODULE_LABELS[form.module] ?? form.module) : "—"}</dd>
                      <dt className="font-medium">Browser</dt><dd>{context.browser} on {context.os}</dd>
                      <dt className="font-medium">Screen</dt><dd>{context.viewport}</dd>
                      <dt className="font-medium">Build</dt><dd>{context.appVersion}</dd>
                      <dt className="font-medium">Errors</dt>
                      <dd>
                        {context.consoleErrors.length === 0 ? "None recorded" : (
                          <ul className="max-h-28 list-none space-y-0.5 overflow-auto font-mono text-[11px]">
                            {context.consoleErrors.map((e, i) => <li key={i} className="break-all">{e}</li>)}
                          </ul>
                        )}
                      </dd>
                    </dl>
                  )}
                </div>
              )}

              {/* File attachments */}
              <div>
                <FileInput
                  multiple accept="image/*,.pdf,.docx" placeholder="Attach files"
                  onFilesChange={picked => setFiles(prev => [...prev, ...picked])}
                />
                {files.length > 0 && (
                  <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {files.map((f, i) => (
                      <span key={i} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, background: "var(--color-surface-100,#f3f4f6)", padding: "2px 8px", borderRadius: 6 }}>
                        {f.name}
                        <button
                          type="button"
                          onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))}
                          style={{ background: "none", border: "none", cursor: "pointer", color: "var(--color-text-secondary,#6b7280)", padding: 0, lineHeight: 1 }}
                        >×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {error && (
                <div style={{ fontSize: 13, color: "var(--color-error)", background: "var(--color-error-bg-subtle)", padding: "8px 12px", borderRadius: 6 }}>
                  {error}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sticky footer (hidden in annotation mode) */}
        {!annotationMode && (
          <div style={{
            display: "flex",
            gap: 10,
            padding: "12px 20px",
            borderTop: "1px solid var(--color-border,#e5e7eb)",
            flexShrink: 0,
          }}>
            <button onClick={handleClose} style={{ ...secondaryBtnStyle, flex: 1 }}>
              Cancel
            </button>
            <AsyncButton
              onClick={() => handleSubmit()}
              disabled={submitting}
              className="flex-[2]"
            >
              Submit Issue
            </AsyncButton>
          </div>
        )}
      </div>
    </>
  );
}

const secondaryBtnStyle: React.CSSProperties = {
  padding: "8px 14px",
  borderRadius: 8,
  border: "1px solid var(--color-border,#e5e7eb)",
  background: "transparent",
  cursor: "pointer",
  fontSize: 13,
  fontWeight: 500,
};
